<template>
  <div>
    <v-alert type="info" text dense>
      Déclarez les éléments externes que ce module peut recevoir. Chaque connexion décrit un seul type
      d’élément et le traitement appliqué après son dépôt.
    </v-alert>

    <v-switch
      v-model="local.enabled"
      label="Autoriser la réception d’éléments déposés"
      @change="emitValue"
    />

    <template v-if="local.enabled">
      <draggable
        :list="local.connections"
        handle=".connection-handle"
        class="drop-connections"
        @change="emitValue"
      >
        <v-card
          v-for="(connection, connectionIndex) in local.connections"
          :key="connection._editorKey"
          outlined
          class="mb-3"
        >
          <v-card-title class="py-2 px-3 subtitle-2">
            <v-icon small class="connection-handle mr-2">mdi-drag</v-icon>
            <span>{{ connection.name || `Connexion ${connectionIndex + 1}` }}</span>
            <v-spacer />
            <v-switch
              v-model="connection.enabled"
              dense
              hide-details
              class="mt-0 mr-2"
              label="Active"
              @change="emitValue"
            />
            <v-btn icon small color="error" title="Supprimer cette connexion" @click="removeConnection(connectionIndex)">
              <v-icon small>mdi-delete-outline</v-icon>
            </v-btn>
          </v-card-title>

          <v-divider />

          <v-card-text class="pb-2">
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field v-model="connection.name" label="Nom de la connexion" outlined dense hide-details="auto" @input="emitValue" />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="connection.channel"
                  label="Canal"
                  hint="Doit correspondre au canal émis par la source."
                  persistent-hint outlined dense @input="emitValue"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="connection.entityType"
                  label="Type d’élément"
                  hint="Une seule valeur par connexion."
                  persistent-hint outlined dense @input="emitValue"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="connection.sourceModules"
                  label="Modules sources autorisés"
                  hint="Facultatif. IDs séparés par des virgules."
                  persistent-hint outlined dense @input="emitValue"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="connection.mode"
                  :items="modes"
                  label="Mode"
                  outlined dense clearable hide-details="auto" @change="emitValue"
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="connection.page"
                  :items="pages"
                  label="Page ouverte"
                  outlined dense clearable hide-details="auto" @change="emitValue"
                />
              </v-col>
            </v-row>

            <div class="d-flex align-center mt-4 mb-2">
              <div class="subtitle-2">Champs renseignés après le dépôt</div>
              <v-spacer />
              <v-btn small outlined color="primary" @click="addMapping(connection)">
                <v-icon left small>mdi-plus</v-icon>Ajouter un champ
              </v-btn>
            </div>

            <v-alert v-if="!connection.mappings.length" text dense type="info" class="mb-2">
              Aucun champ n’est encore renseigné par cette connexion.
            </v-alert>

            <draggable :list="connection.mappings" handle=".mapping-handle" @change="emitValue">
              <v-row
                v-for="(mapping, mappingIndex) in connection.mappings"
                :key="mapping._editorKey"
                dense align="center"
              >
                <v-col cols="auto">
                  <v-icon small class="mapping-handle">mdi-drag</v-icon>
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="mapping.field"
                    label="Champ cible"
                    placeholder="productionsCmd_cls_lines_id"
                    outlined dense hide-details="auto" @input="emitValue"
                  />
                </v-col>
                <v-col cols="12" md="3">
                  <v-select
                    v-model="mapping.type"
                    :items="valueTypes"
                    label="Type de donnée"
                    outlined dense hide-details="auto" @change="emitValue"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="mapping.value"
                    label="Valeur ou variable"
                    placeholder="[[drag||entityId]]"
                    outlined dense hide-details="auto" @input="emitValue"
                  />
                </v-col>
                <v-col cols="auto">
                  <v-btn icon small color="error" title="Supprimer ce champ" @click="removeMapping(connection, mappingIndex)">
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </draggable>
          </v-card-text>
        </v-card>
      </draggable>

      <v-btn small color="primary" @click="addConnection">
        <v-icon left small>mdi-plus</v-icon>Ajouter une connexion
      </v-btn>

      <v-alert type="info" text dense class="mt-4 mb-0">
        Variables source : <code>[[drag||entityId]]</code>, <code>[[drag||quantity]]</code> ou
        <code>[[drag||nom_alias]]</code>. Variables de destination : <code>[[drop||start]]</code>,
        <code>[[drop||end]]</code>, <code>[[drop||allDay]]</code> ou <code>[[drop||cardId]]</code>.
      </v-alert>
    </template>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { emptyDropReceiver } from './dropReceiver'

let editorKey = 0

function nextEditorKey(prefix) {
  editorKey += 1
  return `${prefix}-${editorKey}`
}

function toList(value) {
  if (Array.isArray(value)) return value.map(item => String(item).trim()).filter(Boolean)
  if (value === null || value === undefined || value === '') return []
  return String(value).split(',').map(item => item.trim()).filter(Boolean)
}

function normalizeMappings(value) {
  let source = value
  if (typeof source === 'string') {
    try { source = JSON.parse(source) } catch (error) { source = [] }
  }
  if (source && !Array.isArray(source) && typeof source === 'object') {
    source = Object.keys(source).map(field => ({ field, value: source[field] }))
  }
  return (Array.isArray(source) ? source : []).map(mapping => ({
    field: (mapping && (mapping.field || mapping.key || mapping.target)) || '',
    type: (mapping && (mapping.type || mapping.typedata)) || 'text',
    value: mapping && mapping.value !== undefined ? mapping.value : (mapping && mapping.source) || '',
    _editorKey: nextEditorKey('mapping'),
  }))
}

export default {
  name: 'DropReceiverAdmin',
  components: { draggable },
  props: {
    value: { type: Object, default: null },
    pages: { type: Array, default: () => [] },
  },
  data() {
    return {
      local: this.normalize(this.value),
      emitting: false,
      modes: [
        { text: 'Insertion', value: 'insert' },
        { text: 'Mise à jour', value: 'update' },
        { text: 'Affectation / répartition', value: 'allocation' },
      ],
      valueTypes: [
        { text: 'Nombre', value: 'number' },
        { text: 'Texte', value: 'text' },
        { text: 'Booléen', value: 'bool' },
      ],
    }
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        if (!this.emitting) this.local = this.normalize(value)
      },
    },
  },
  methods: {
    normalize(value) {
      const defaults = emptyDropReceiver()
      const receiver = value && typeof value === 'object' ? { ...defaults, ...value } : defaults
      const connections = Array.isArray(receiver.connections) ? receiver.connections : []

      return {
        ...receiver,
        connections: connections.reduce((result, connection) => {
          const types = toList(connection && (connection.entityType || connection.entityTypes))
          const entityTypes = types.length ? types : ['']
          entityTypes.forEach(entityType => result.push(this.normalizeConnection(connection, entityType)))
          return result
        }, []),
      }
    },
    normalizeConnection(connection = {}, entityType = '') {
      return {
        ...connection,
        enabled: connection.enabled !== false,
        name: connection.name || '',
        channel: connection.channel || '',
        entityType,
        sourceModules: Array.isArray(connection.sourceModules) ? connection.sourceModules.join(',') : (connection.sourceModules || ''),
        mode: connection.mode || 'insert',
        page: connection.page || '',
        mappings: normalizeMappings(connection.mappings),
        _editorKey: nextEditorKey('connection'),
      }
    },
    serializedConnection(connection) {
      const values = { ...connection }
      const entityType = values.entityType
      const mappings = values.mappings
      delete values._editorKey
      delete values.entityType
      delete values.mappings
      return {
        ...values,
        entityTypes: entityType || '',
        mappings: mappings.map(mapping => ({
          field: mapping.field || '',
          type: mapping.type || 'text',
          value: mapping.value,
        })),
      }
    },
    emitValue() {
      this.emitting = true
      this.$emit('input', {
        ...this.local,
        connections: this.local.connections.map(this.serializedConnection),
      })
      this.$nextTick(() => { this.emitting = false })
    },
    addConnection() {
      this.local.connections.push(this.normalizeConnection())
      this.emitValue()
    },
    removeConnection(index) {
      this.local.connections.splice(index, 1)
      this.emitValue()
    },
    addMapping(connection) {
      connection.mappings.push({ field: '', type: 'text', value: '', _editorKey: nextEditorKey('mapping') })
      this.emitValue()
    },
    removeMapping(connection, index) {
      connection.mappings.splice(index, 1)
      this.emitValue()
    },
  },
}
</script>

<style scoped>
.connection-handle,
.mapping-handle {
  cursor: grab;
}

.connection-handle:active,
.mapping-handle:active {
  cursor: grabbing;
}
</style>
